import routes from 'common/routes';
import { isAdmin, isSupervisor } from 'common/utils/user';

/**
 * Destino de cada notificación al hacer clic. Centraliza la navegación que antes estaba dispersa
 * (y rota: el RouterLink vivía dentro de un MUI Menu, que se quedaba abierto y "tapaba" el cambio).
 * Ahora la navegación es programática desde el ítem del menú.
 *
 * @param  {Object}  notification  { type, data, ... }
 * @param  {string}  role          rol del usuario actual
 * @returns {string|null}          ruta destino, o null si no aplica
 */
export const resolveNotificationPath = (notification, role) => {
  const { type } = notification || {};
  // El default de desestructuración solo cubre `undefined`: con `data: null` (backend sin
  // payload) cualquier lectura de abajo lanzaba y el clic de la fila se perdía entero.
  const data = notification?.data || {};
  const admin = isAdmin(role);
  const adminOrSup = admin || isSupervisor(role);

  switch (type) {
    // Publicada o actualizada llevan al mismo sitio: al artículo (o al listado, para quien lo
    // administra). Lo que cambió es el aviso, no el destino.
    case 'news.created':
    case 'news.updated':
      if (adminOrSup) return routes.listNews.path;
      return data.news_id != null ? routes.viewNews.path.replace(':id', data.news_id) : routes.listNews.path;

    case 'session.created':
    case 'session.deleted.client':
    case 'session.deleted.trainer':
    case 'group.session.created':
      return routes.calendar.path;

    case 'exercise.request.created':
      return `${routes.exerciseRequests.path}?tab=0`;
    case 'exercise.request.approved':
      return `${routes.exerciseRequests.path}?tab=${admin ? 1 : 0}`;
    case 'exercise.request.rejected':
      return `${routes.exerciseRequests.path}?tab=${admin ? 2 : 1}`;

    case 'plan.acquisition_requested':
      return routes.planRequests.path;
    case 'plan.request.approved':
      return routes.myPlanStatus.path;
    case 'plan.request.rejected':
      return routes.clientDashboard.path;

    case 'user.registered':
      return data.user?.id != null ? routes.editClient.path.replace(':id', data.user.id) : null;

    // PQRS: el admin responde desde su bandeja; el cliente revisa su solicitud.
    case 'pqrs.created':
      return adminOrSup ? routes.adminPqrs.path : routes.myPqrs.path;
    case 'pqrs.replied':
      return routes.myPqrs.path;

    // Progreso físico: el cliente ve su historial; al equipo el backend le manda en `action_url`
    // la ficha del cliente con la pestaña de progreso físico abierta.
    case 'progress.improved':
    case 'progress.observation':
    case 'progress.submitted':
    case 'progress.data_added':
      return data.action_url || routes.clientProgress.path;

    // OTRIX Play (antes caían al default y no navegaban a ningún lado).
    case 'play.routine_adopted':
      return routes.playForum.path;
    case 'play.result_reviewed':
      return routes.playPerformance.path;
    case 'play.routine_moderated':
      return routes.playForum.path;

    // Bienestar corporativo: solicitud de plan de una empresa; el admin la revisa en su ficha.
    case 'organization.plan_requested':
      return data.action_url || routes.adminOrganizations.path;

    // Bienestar corporativo: la alerta apunta a la ficha de la organización (solo admin).
    case 'wellbeing.alert':
      return data.action_url || (adminOrSup ? routes.adminOrganizations.path : routes.myWellbeing.path);

    default:
      // Gamificación (reward_approved/rank_up/top_3_monthly/…) y genéricas traen su propia URL.
      return data.action_url || null;
  }
};

/**
 * Tipos cuyo evento CADUCA: pasada la fecha del propio payload, el destino ya no tiene nada
 * que mostrar (la sesión ocurrió, el premio venció, el ranking cerró el mes). Se detecta sin
 * pegarle al servidor, mirando la fecha que la notificación ya trae consigo.
 *
 * `field` es la clave del payload y `kind` decide el mensaje que ve el usuario.
 */
const EXPIRING_TYPES = {
  'session.created': { field: 'start_date', kind: 'session' },
  'group.session.created': { field: 'start_date', kind: 'session' },
  reward_approved: { field: 'expires_at', kind: 'reward' },
  top_3_monthly: { field: 'month', kind: 'month' },
  top3_monthly: { field: 'month', kind: 'month' },
  weekly_summary: { field: 'month', kind: 'month' }
};

/** Fin del periodo que representa un valor de payload (una fecha suelta o un mes "2026-07"). */
const periodEnd = (raw, kind) => {
  if (!raw) return null;

  if (kind === 'month') {
    const match = /^(\d{4})-(\d{2})/.exec(String(raw));
    if (!match) return null;
    // Primer día del mes SIGUIENTE: el ranking mensual sigue vigente todo el mes.
    return new Date(Number(match[1]), Number(match[2]), 1);
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * ¿El evento de esta notificación ya pasó? Devuelve `{ expired, kind }`; `kind` sirve para
 * elegir el texto ("esa sesión ya pasó" / "ese premio venció" / "ese ranking ya cerró").
 *
 * Las notificaciones de tipos que NO caducan (o sin fecha en el payload) nunca se marcan:
 * más vale navegar de más que esconderle al usuario algo que sí existe.
 */
export const getNotificationExpiry = (notification) => {
  const { type } = notification || {};
  // Mismo motivo que en `resolveNotificationPath`: `data` puede llegar como `null`.
  const data = notification?.data || {};
  const rule = EXPIRING_TYPES[type];
  if (!rule) return { expired: false, kind: null };

  const end = periodEnd(data[rule.field], rule.kind);
  if (!end) return { expired: false, kind: null };

  return { expired: end.getTime() < Date.now(), kind: rule.kind };
};

/** Texto que se le muestra al usuario cuando la notificación apunta a algo que ya pasó. */
export const expiredNotificationMessage = (t, kind) => {
  if (kind === 'reward') {
    return t('expired.reward', { ns: 'notifications', defaultValue: 'Ese premio ya venció, así que no hay nada que reclamar.' });
  }
  if (kind === 'month') {
    return t('expired.month', { ns: 'notifications', defaultValue: 'Ese ranking ya cerró: mira el del mes en curso.' });
  }
  return t('expired.session', { ns: 'notifications', defaultValue: 'Esa sesión ya pasó, por eso no la encuentras en la agenda.' });
};

export default resolveNotificationPath;
